import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

// =========================================================
// AI rail scope context
// =========================================================
//
// The AI rail lives in AppLayout and is mounted once per project route. A
// scene that wants the rail rebound to its own context (different eyebrow,
// different seed thread) calls `setScope({ kind: 'custom', label: '…' })` on
// mount and resets to `{ kind: 'project' }` on unmount. The AIRail reads
// `scope` on every render to decide what to show.

export type AIRailScope =
  | { kind: 'project' }
  | { kind: 'custom'; label: string };

type AIRailScopeValue = {
  scope: AIRailScope;
  setScope: (scope: AIRailScope) => void;
  resetScope: () => void;
};

const AIRailScopeContext = createContext<AIRailScopeValue | null>(null);

export function AIRailScopeProvider({ children }: { children: ReactNode }) {
  const [scope, setScopeState] = useState<AIRailScope>({ kind: 'project' });

  const setScope = useCallback((next: AIRailScope) => {
    setScopeState(next);
  }, []);

  const resetScope = useCallback(() => {
    setScopeState({ kind: 'project' });
  }, []);

  const value = useMemo(
    () => ({ scope, setScope, resetScope }),
    [scope, setScope, resetScope],
  );

  return (
    <AIRailScopeContext.Provider value={value}>
      {children}
    </AIRailScopeContext.Provider>
  );
}

export function useAIRailScope(): AIRailScopeValue {
  const ctx = useContext(AIRailScopeContext);
  if (!ctx) {
    // Fail-safe default — rail renders project scope if the provider is
    // missing (test harness, storybook-style mounts).
    return {
      scope: { kind: 'project' },
      setScope: () => {},
      resetScope: () => {},
    };
  }
  return ctx;
}
